/* =========================================================================
   Admin Development-Plan editor — wire DTOs + redux slice shape.

   These mirror pm/devplan/admin_serializers.py 1:1 (flat snake_case, no
   camelize shim). Unlike the trainee /me payload (code-keyed), the admin graph
   is ID-BASED: every FK (track / from_node / to_node / node / scenario_asset /
   game_asset / target_node) is an int id. `adaptContentToLayout` converts this
   to the code-keyed trainee shape only for canvas rendering.
   ========================================================================= */

export type DevPlanStatus = 'draft' | 'published';
export type DevEdgeKind = 'prereq' | 'feed' | 'soft';
export type DevAssetKind = 'scenario' | 'game' | 'ladder';
export type DevGameRole = 'primary' | 'reuse';

/** The four canonical tiers (curriculum knob; drives the XP ladder + badge). */
export const TIER_OPTIONS = ['foundational', 'core', 'advanced', 'mastery'];

export interface DevPlanAdmin {
  id: number;
  code: string;
  name: string;
  description: string;
  status: DevPlanStatus;
  hidden: boolean;
  is_default: boolean;
  order: number;
  family: string;
  version: number;
  cloned_from: number | null;
  created_by: number | null;
  created: string;
  updated: string;
}

export interface DevTrackAdmin {
  id: number;
  plan: number;
  code: string;
  name: string;
  blurb: string;
  color: string;
  order: number;
}

export interface DevClusterAdmin {
  id: number;
  plan: number;
  track: number;
  code: string;
  name: string;
  order: number;
}

/** One PM blueprint a node can be played on (assessment-backed nodes). */
export interface NodeBlueprintRef {
  slug: string;
  name: string;
  difficulty: string; // Easy | Moderate | Hard | Very hard
  assets: number;
  news: number;
}

/** Node.detail — server-owned, display-only. HTML-backed nodes carry the
 *  scorecard / pass bars; assessment-backed nodes (Idea Generator) carry the
 *  cluster's blueprint list plus the rank -> blueprint-slug ladder. */
export interface DevNodeDetail {
  pass_bars?: { rank: number; bar: string }[];
  scorecard?: { label: string; max: number; rank: number }[];
  blueprints?: NodeBlueprintRef[];
  rank_ladder?: Record<string, string>;
}

export interface DevNodeAdmin {
  id: number;
  plan: number;
  track: number;
  internal_code: string; // locked: HTML refId (scoring identity)
  code: string; // editable display badge
  group: string; // cluster code ('A'..'E')
  tier: string;
  name: string;
  description: string;
  meter_name: string | null;
  meter_signed: boolean;
  meter_low_pole: string | null;
  meter_high_pole: string | null;
  required_level: number;
  skill_points_cost: number;
  max_rank: number; // locked: HTML rank-count
  scenario_asset: number | null; // locked: assign-HTML is v2
  detail: DevNodeDetail | null;
  is_active: boolean;
  order: number;
}

export interface DevEdgeAdmin {
  id: number;
  plan: number;
  from_node: number;
  to_node: number;
  kind: DevEdgeKind;
  required_rank: number;
}

export interface DevNodeGameAdmin {
  id: number;
  plan: number;
  node: number;
  game_asset: number;
  role: DevGameRole;
}

export interface RemediationRouteAdmin {
  id: number;
  plan: number;
  node: number; // locked (per-node gate binding)
  gate_key: string; // locked (HTML-owned vocabulary)
  gate_name: string;
  target_game: number | null;
  target_node: number | null;
}

export interface DevAssetAdmin {
  id: number;
  key: string;
  kind: DevAssetKind;
  file_path: string;
  emitted_ref_id: string | null;
  title: string;
  tier: string | null;
  tags: string | null;
  is_built: boolean;
  category: string | null;
  difficulty: string | null;
  minutes: number | null;
  blurb: string | null;
  skills: string | null;
  icon: string | null;
}

/** Response of GET /devplan/admin/plans/<code>/content — the full editable graph. */
export interface DevPlanContent {
  plan: DevPlanAdmin;
  tracks: DevTrackAdmin[];
  clusters: DevClusterAdmin[];
  nodes: DevNodeAdmin[];
  edges: DevEdgeAdmin[];
  node_games: DevNodeGameAdmin[];
  remediation: RemediationRouteAdmin[];
}

/* ---- Reviewed advice and isolated demo boosts ------------------------- */

export interface DevRecommendationItem {
  rank: number;
  node_code: string;
  blueprint_slug: string;
  rationale: string;
  available?: boolean;
  unavailable_reason?: string;
}

export interface DevRecommendationAdmin {
  id: number;
  user: number;
  username: string;
  plan: number;
  plan_code: string;
  session: number;
  session_label: string;
  mode: 'advisory' | 'boost';
  content_revision?: string;
  items: DevRecommendationItem[];
  available?: boolean;
  unavailable_reason?: string;
  enabled: boolean;
  node_codes: string[];
  node_count: number;
  created: string;
  updated: string;
  /** Only on generate / set_enabled responses: how many nodes the roll actually unlocked.
   *  The single signal separating a good roll from an inert one. */
  granted_count?: number;
}

/** One row of GET /devplan/admin/recommendations/targets — a trainee who can reach the
 *  Development Plan tab, plus the sessions they played. */
export interface DevRecommendationTarget {
  user_id: number;
  username: string;
  sessions: { id: number; label: string; status: number }[];
}

export interface DevPlanAdminState {
  plans: DevPlanAdmin[]; // rail list
  plansFetched: boolean;
  content?: DevPlanContent; // the open plan's graph (id-based)
  contentCode?: string; // which plan `content` holds
  contentFetched: boolean;
  assets: DevAssetAdmin[]; // global pool (for game / scenario pickers)
  assetsFetched: boolean;
  saving: number; // in-flight write count → "Saving…" chip
  error?: string;
  publishProblems?: string[]; // from a 409 publish; cleared on next publish / content load
  // Reviewed advice and demo boosts — scoped to the plan last fetched for.
  recommendations: DevRecommendationAdmin[];
  recommendationsCode?: string; // which plan `recommendations` holds
  recTargets: DevRecommendationTarget[];
  recTargetsFetched: boolean;
  /** granted_count of the most recent generate/toggle, so the panel can show the roll quality. */
  lastRecGranted?: number;
}
